import { Button } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useEffect, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate, useNavigationType } from 'react-router';

/** The dev-only preview mounts the portal under this prefix; real portal routes live at the root. */
export const PREVIEW_BASE = '/__preview/portal';

/** The portal pages a detail page leads back to, as the back button names them. */
const PAGES = {
  '/': 'Dashboard',
  '/requisitions': 'Dossier Requisition',
  '/requests': 'All Dossiers',
  '/downloads': 'Downloads',
  '/notifications': 'Notifications',
  '/account': 'Account',
} as const;
type PortalPage = keyof typeof PAGES;

const isPage = (path: string): path is PortalPage => Object.hasOwn(PAGES, path);

/**
 * Where the client has been in this tab: the portal page they last opened something from (with its view and search),
 * the current location, and the history entry before it when the portal put it there (unknown after a reload or a
 * browser back). Paths are relative to the portal root.
 */
const trail: { page: string | null; current: string | null; before: string | null } = { page: null, current: null, before: null };

/** Keeps the trail; the portal layout runs it on every navigation. */
export function usePortalTrail(path: string, search: string) {
  const type = useNavigationType();
  useEffect(() => {
    const here = path + search;
    if (here === trail.current) return;
    // A replace keeps the entry before it; a browser back or forward leaves it unknown.
    if (type === 'PUSH') trail.before = trail.current;
    else if (type === 'POP') trail.before = null;
    trail.current = here;
    if (isPage(path)) trail.page = here;
  }, [path, search, type]);
}

/** The portal page the client last opened something from, without its view and search; null when they arrived directly. */
export function portalOrigin(): string | null {
  return trail.page?.split('?')[0] ?? null;
}

/** Where "back" goes from a detail page: the page the client came from, or `fallback` when they arrived directly. */
export function usePortalBack(fallback: PortalPage) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const base = pathname.startsWith(PREVIEW_BASE) ? PREVIEW_BASE : '';
  const target = trail.page ?? fallback;
  const [targetPath] = target.split('?');
  const to = base + (base && targetPath === '/' ? target.slice(1) : target);
  const onClick = (e: MouseEvent) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    // Straight back when the previous entry is that page, so history does not collect a second copy of it.
    if (trail.before !== null && trail.before === trail.page) {
      e.preventDefault();
      navigate(-1);
    }
  };
  return { to, onClick, label: `Back To ${PAGES[isPage(targetPath) ? targetPath : fallback]}` };
}

/** The back button at the top of a detail page. */
export function BackButton({ fallback }: { fallback: PortalPage }) {
  const back = usePortalBack(fallback);
  return (
    <Button
      component={Link}
      to={back.to}
      onClick={back.onClick}
      variant="default"
      size="xs"
      fz="sm"
      leftSection={<IconArrowLeft size={15} />}
      w="fit-content"
    >
      {back.label}
    </Button>
  );
}
